import React from 'react'
import PropTypes from 'prop-types'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import { Box, Popper, ClickAwayListener } from '@material-ui/core'
import Toggle from 'react-toggle'

import { Card, CardHeading, CardContent } from '../layout/Card'
import { Projection } from '../Projection'
import { buildProjectionDatasets } from './datasets'
import { track } from '@utils/tracking'

import { TimeValueToggle } from '../TimeValueToggle'
import { TimeValuePropType } from '@hooks/useTimeValue'
import { Hider } from '@components/HOCs/Hider'

export const ChartTypes = {
  income: "income",
  capital: "capital"
}

export const ForecastCard = (props) => {
  const [state, setState] = React.useState({
    chartType: ChartTypes.capital,
    retirementAge: props.retirementAge,
    showTotalExpense: false,
    showGrossIncome: false
  })

  const setChartType = (chartType) => (evt) => {
    evt.preventDefault()
    track('Forecast', `Set chart type to ${chartType}`)

    setState(previousState => ({
      ...previousState,
      chartType: chartType
    }))
  }

  const setShowGrossIncome = (value) => {
    track('Forecast', `Set show gross income to ${value}`)
    setState(previousState => ({
      ...previousState,
      showGrossIncome: value
    }))
  }

  const setShowTotalExpense = (value) => {
    track('Forecast', `Set show total expense to ${value}`)
    setState(previousState => ({
      ...previousState,
      showTotalExpense: value
    }))
  }

  const resetToComparativeView = () => {
    track('Forecast', `Reset to comparative view`)
    setState(previousState => ({
      ...previousState,
      showGrossIncome: false,
      showTotalExpense: false
    }))
  }

  const isScenario = props.forecasts.scenarioForecast
  // This is a very expensive call that can potentially
  // be memoized if we have some perf issues here
  const datasets = buildProjectionDatasets(state, props)

  return (
    <Card className='ui-card ui-card--projections-chart'>
      <CardHeading className={`${state.chartType === ChartTypes.capital ? 'items-center sm:items-start' : ''}`}>
        <ForecastHeader
          clientPronoun={props.clientPronoun}
          chartType={state.chartType}
          isScenario={isScenario}
          setChartType={setChartType}
          showTotalExpense={state.showTotalExpense}
          showGrossIncome={state.showGrossIncome}
          setShowTotalExpense={setShowTotalExpense}
          setShowGrossIncome={setShowGrossIncome}
          resetToComparativeView={resetToComparativeView}
        />
      </CardHeading>
      <CardContent>
        <Projection
          datasets={datasets}
          colors={props.colors}
          chartType={state.chartType}
          timeValue={props.timeValue}
        />
      <div className='flex sm:hidden justify-center'>
        <TimeValueToggle
          setTimeValue={props.setTimeValue}
          timeValue={props.timeValue}
        />
      </div>
      </CardContent>
    </Card>
  )
}

const ForecastHeader = ({
  clientPronoun, chartType, setChartType, isScenario,
  showGrossIncome, showTotalExpense, setShowGrossIncome, setShowTotalExpense,
  resetToComparativeView
}) => {
  const { menuAnchorElement, toggleMenu, closeMenu, menuVisible, menuId } = useIncomeChartsMenu()
  return (
    <>
      <h5>{clientPronoun} {chartType === ChartTypes.income ? 'Income' : 'Capital'} Projection</h5>
      <Box className='flex justify-between items-center mt-2'>
        <div className='chart-actions'>
          <a className={chartType === ChartTypes.income ? '' : 'active'} onClick={setChartType(ChartTypes.capital)}>
            Capital Chart
          </a>
          <a className={chartType === ChartTypes.income ? 'active' : ''} onClick={setChartType(ChartTypes.income)}>
            Income Chart
          </a>
        </div>
        { chartType === ChartTypes.income &&
        <Box className='flex items-center gap-4'>
          { (showGrossIncome || showTotalExpense) && isScenario &&
            <small className='hidden sm:block'>
              <a onClick={resetToComparativeView}>
                Reset to comparative view
              </a>
            </small>
          }
          <MoreVertIcon
            aria-describedby={menuId}
            onClick={toggleMenu}
            style={{ cursor: 'pointer' }} />
          <Popper
            id={menuId}
            open={menuVisible}
            anchorEl={menuAnchorElement}
            placement='bottom-end'
          >
            <ClickAwayListener onClickAway={closeMenu}>
              <Box>
                <Hider show={menuVisible} withTransition
                  render={() => (
                    <Card>
                      <CardHeading>
                        <Box className={'flex justify-between gap-8'}>
                          <p>View total expense</p>
                          <Toggle
                            checked={showTotalExpense}
                            onChange={(e) => setShowTotalExpense(e.target.checked)}
                            icons={false}
                            className='app-toggle'
                          />
                        </Box>
                        <Box className={'flex justify-between gap-8'}>
                          <p>View gross income</p>
                          <Toggle
                            checked={showGrossIncome}
                            onChange={(e) => setShowGrossIncome(e.target.checked)}
                            icons={false}
                            className='app-toggle'
                          />
                        </Box>
                      </CardHeading>
                    </Card>
                  )}
                />
              </Box>
            </ClickAwayListener>
          </Popper>
        </Box>}
      </Box>
      { (showGrossIncome || showTotalExpense) && isScenario &&
        <small className='sm:hidden text-right'>
          <a onClick={resetToComparativeView}>
            Reset to comparative view
          </a>
        </small>
      }
    </>
  )
}

const useIncomeChartsMenu = () => {
  const [menuAnchorElement, setMenuAnchorElement] = React.useState()
  const toggleMenu = (event) => {
    track('Forecast', `Toggle income breakdown menu`)
    setMenuAnchorElement(menuAnchorElement ? null : event.currentTarget)
  }
  const closeMenu = (event) => {
    track('Forecast', `Close income breakdown menu`)
    event && event.preventDefault()
    setMenuAnchorElement(null)
  }
  const menuVisible = Boolean(menuAnchorElement)
  const menuId = open ? 'plan-actions-popover-menu' : undefined

  return {
    menuAnchorElement,
    toggleMenu,
    closeMenu,
    menuVisible,
    menuId
  }
}

ForecastCard.propTypes = {
  /* Global config */
  colors: PropTypes.object,
  clientPronoun: PropTypes.string,
  assumptions: PropTypes.object,
  usePresentValue: PropTypes.bool,
  timeValue: TimeValuePropType.isRequired,
  setTimeValue: PropTypes.func,

  /* Component props */
  retirementAge: PropTypes.number,
  currentAge: PropTypes.number,
  forecasts: PropTypes.exact({
    actualForecast: PropTypes.arrayOf(PropTypes.object).isRequired,
    scenarioForecast: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  showTicksInYear: PropTypes.bool,
  cashflowEvents: PropTypes.arrayOf(PropTypes.object),
}
